import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import type { User as SupabaseUser } from '@supabase/supabase-js'
import { supabase, handleAuthError, getCurrentUserProfile } from '../lib/supabase'
import { clearUserContext } from '../lib/monitoring'
import { queryClient } from '../lib/react-query'
import { isDemoAccount } from '../lib/demo'
import { sendWelcomeEmail } from '../lib/email'
import type { User, Profile, AuthContextType, UserRole } from '../types/database'
import { startSessionExpiryMonitoring, stopSessionExpiryMonitoring } from '../lib/sessionExpiryHandler'
import { KNOWN_ORGS } from '../config/organizations'

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Auth provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const lastProfileLoadRef = useRef<number>(0)
  const profileLoadingRef = useRef<boolean>(false)
  const [organizationRole, setOrganizationRole] = useState<'admin' | 'staff' | 'viewer' | 'member' | null>(null)
  const sessionCheckTimeoutRef = useRef<NodeJS.Timeout>()
  const refreshLockRef = useRef<boolean>(false)
  const initRef = useRef<boolean>(false)
  const sessionCheckRef = useRef<boolean>(false)
  
  // Cache profile in localStorage to survive temporary failures
  const cacheProfile = (profile: Profile | null) => {
    if (profile) {
      const profileWithTimestamp = {
        ...profile,
        cached_at: new Date().toISOString()
      };
      localStorage.setItem('cached_profile', JSON.stringify(profileWithTimestamp))
    }
  }
  
  const getCachedProfile = (): Profile | null => {
    try {
      const cached = localStorage.getItem('cached_profile')
      return cached ? JSON.parse(cached) : null
    } catch {
      return null
    }
  }

  // Initialize auth state
  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (initRef.current) {
      console.log('🔐 Auth already initialized, skipping')
      return
    }
    initRef.current = true
    
    // Skip auth initialization on activation pages
    if (window.location.pathname.includes('/customer/activate/')) {
      console.log('🔐 On activation page, skipping auth initialization')
      setLoading(false)
      return
    }
    
    // Get initial session
    getInitialSession()

    // Safety timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.log('🔐 Loading timeout reached, forcing loading to false')
      if (loading) {
        setLoading(false)
        // If we still don't have a user after timeout, check localStorage
        if (!user) {
          const cachedProfile = getCachedProfile()
          if (cachedProfile) {
            console.log('🔐 Using cached profile after timeout')
            setProfile(cachedProfile)
            setUser({
              id: cachedProfile.id,
              email: cachedProfile.email,
              profile: cachedProfile
            })
          }
        }
      }
    }, 10000) // 10 second timeout

    // Set up proactive session refresh
    const refreshInterval = setInterval(async () => {
      // Check if we should refresh
      if (refreshLockRef.current || !user) return
      
      const { data: { session }, error } = await supabase.auth.getSession()
      if (session && !error) {
        const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
        const now = Date.now()
        const timeUntilExpiry = expiresAt - now
        
        // Refresh if less than 5 minutes until expiry
        if (timeUntilExpiry < 5 * 60 * 1000) {
          console.log('🔐 Session expiring soon, refreshing proactively...')
          refreshLockRef.current = true
          setIsRefreshing(true)
          
          try {
            const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession()
            if (newSession && !refreshError) {
              console.log('🔐 Session refreshed successfully')
              // Store refreshed session
              localStorage.setItem('facility-pro-auth-backup', JSON.stringify({
                access_token: newSession.access_token,
                refresh_token: newSession.refresh_token,
                expires_at: newSession.expires_at,
                user: newSession.user,
                timestamp: new Date().toISOString()
              }))
            } else {
              console.error('🔐 Session refresh failed:', refreshError)
            }
          } finally {
            refreshLockRef.current = false
            setIsRefreshing(false)
          }
        }
      }
    }, 60 * 1000) // Check every minute instead of 4 minutes

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Auth state change:', event, {
          hasSession: !!session,
          userId: session?.user?.id,
          currentUserId: user?.id,
          loading
        })
        
        // Store session for recovery
        if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          localStorage.setItem('facility-pro-auth-backup', JSON.stringify({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
            expires_at: session.expires_at,
            user: session.user,
            timestamp: new Date().toISOString()
          }))
          
          // Start session expiry monitoring when signed in
          if (event === 'SIGNED_IN') {
            startSessionExpiryMonitoring()
          }
        }
        
        
        // Ignore certain events to prevent unnecessary actions
        if (event === 'TOKEN_REFRESHED' && user && profile) {
          console.log('🔐 Token refreshed, maintaining current state')
          return
        }
        
        // Handle sign out
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setUser(null)
          setProfile(null)
          localStorage.removeItem('facility-pro-auth-backup')
          localStorage.removeItem('cached_profile')
          setLoading(false)
          // Stop session monitoring when signed out
          stopSessionExpiryMonitoring()
          return
        }
        
        // For other events, only handle if necessary
        if (session?.user && event !== 'INITIAL_SESSION') {
          // Check if this is actually a new session or user change
          const isNewUser = !user || user.id !== session.user.id
          const needsProfile = !profile || profile.id !== session.user.id
          
          // Only reload profile if truly necessary and not during initial load
          if ((isNewUser || needsProfile) && !loading) {
            console.log('🔐 Loading profile for event:', event)
            await loadUserProfile(session.user)
          } else {
            console.log('🔐 Skipping unnecessary profile reload for:', event, { loading, isNewUser, needsProfile })
          }
        }
      }
    )

    // Listen for manual auth refresh events
    const handleAuthRefresh = async () => {
      console.log('🔐 Manual auth refresh triggered')
      if (!loading && !sessionCheckRef.current) {
        await getInitialSession()
      }
    }
    
    window.addEventListener('auth-state-refreshed', handleAuthRefresh)
    
    return () => {
      clearTimeout(timeout)
      clearInterval(refreshInterval)
      subscription.unsubscribe()
      window.removeEventListener('auth-state-refreshed', handleAuthRefresh)
      if (sessionCheckTimeoutRef.current) {
        clearTimeout(sessionCheckTimeoutRef.current)
      }
      // Stop session monitoring on unmount
      stopSessionExpiryMonitoring()
    }
  }, [])

  // Get initial session on mount
  const getInitialSession = async () => {
    try {
      console.log('🔐 Getting initial session...')
      
      // Prevent concurrent session checks
      if (sessionCheckRef.current) {
        console.log('🔐 Session check already in progress, skipping')
        return
      }
      sessionCheckRef.current = true
      
      let { data: { session }, error } = await supabase.auth.getSession()
      
      if (error || !session) {
        console.log('🔐 Session error or no session:', error)
        
        // Try to recover from backup
        const backup = localStorage.getItem('facility-pro-auth-backup')
        if (backup) {
          try {
            const parsed = JSON.parse(backup)
            console.log('🔐 Attempting session recovery from backup...')
            
            const { data: recoveredSession, error: recoveryError } = await supabase.auth.setSession({
              access_token: parsed.access_token,
              refresh_token: parsed.refresh_token
            })
            
            if (recoveredSession?.session && !recoveryError) {
              console.log('🔐 Session recovered successfully')
              session = recoveredSession.session
            } else {
              console.error('🔐 Recovery failed:', recoveryError)
              localStorage.removeItem('facility-pro-auth-backup')
            }
          } catch (e) {
            console.error('🔐 Backup parse error:', e)
            localStorage.removeItem('facility-pro-auth-backup')
          }
        }
        
        if (!session) {
          setLoading(false)
          return
        }
      }

      if (session?.user) {
        console.log('🔐 Found existing session for:', session.user.email, {
          hasUserMetadata: !!session.user.user_metadata,
          userMetadata: session.user.user_metadata
        })
        await loadUserProfile(session.user)
        // Start session monitoring for existing sessions
        startSessionExpiryMonitoring()
      } else {
        console.log('🔐 No existing session found')
      }
    } catch (error) {
      console.error('🔐 Session initialization error:', error)
    } finally {
      console.log('🔐 Setting loading to false')
      setLoading(false)
      sessionCheckRef.current = false
    }
  }

  // Track error logging to prevent console spam
  const orgRoleErrorLoggedRef = useRef<string | null>(null)

  // Retry utility with exponential backoff
  const fetchWithRetry = async <T,>(
    fetchFn: () => Promise<T>,
    options: { maxRetries?: number; baseDelayMs?: number; context?: string } = {}
  ): Promise<{ data: T | null; error: Error | null }> => {
    const { maxRetries = 3, baseDelayMs = 1000, context = 'fetch' } = options
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await fetchFn()
        return { data: result, error: null }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error))

        if (attempt < maxRetries) {
          const delay = baseDelayMs * Math.pow(2, attempt)
          console.log(`🔐 Retrying ${context} (attempt ${attempt + 1}/${maxRetries})...`)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
    }

    return { data: null, error: lastError }
  }

  // Load user profile from database
  const loadUserProfile = async (supabaseUser: SupabaseUser, currentProfile = profile) => {
    try {
      console.log('🔐 Loading user profile for:', supabaseUser.email)

      // Check if we already have a valid profile for this user
      if (currentProfile && user && user.id === supabaseUser.id) {
        console.log('🔐 Profile already loaded, skipping reload')
        return
      }

      // Prevent concurrent loads
      if (profileLoadingRef.current) {
        console.log('🔐 Profile load already in progress, skipping')
        return
      }

      // Debounce profile loads - prevent loading within 5 seconds of last load
      const now = Date.now()
      if (lastProfileLoadRef.current && (now - lastProfileLoadRef.current) < 5000) {
        console.log('🔐 Skipping profile load - too soon after last load')
        // Use cached profile
        const cachedProfile = getCachedProfile()
        if (cachedProfile && cachedProfile.id === supabaseUser.id) {
          setProfile(cachedProfile)
          setUser({
            id: supabaseUser.id,
            email: supabaseUser.email || '',
            profile: cachedProfile
          })
        }
        return
      }
      lastProfileLoadRef.current = now
      profileLoadingRef.current = true

      // Get profile using the fixed function
      const profile = await getCurrentUserProfile().catch((err) => {
        console.error('🔐 Profile loading error:', err)
        // Use cached profile if available
        const cachedProfile = currentProfile || getCachedProfile()
        if (cachedProfile && cachedProfile.id === supabaseUser.id) {
          console.log('🔐 Using cached profile due to error')
          return cachedProfile
        }
        // Create a default profile to prevent lockout
        // Check user metadata for organization_id first
        const metadataOrgId = supabaseUser.user_metadata?.organization_id

        // Use default org or metadata org
        const defaultOrgId = metadataOrgId || KNOWN_ORGS.DEFAULT

        return {
          id: supabaseUser.id,
          email: supabaseUser.email || '',
          role: 'admin',
          full_name: supabaseUser.user_metadata?.full_name || supabaseUser.email || '',
          organization_id: metadataOrgId || defaultOrgId,
          primary_organization_id: metadataOrgId || defaultOrgId, // Include both for compatibility
          is_super_admin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      })
      console.log('🔐 Profile loaded:', profile ? { id: profile.id, email: profile.email } : null)

      // Load organization role if not super admin (with retry logic)
      let orgRole: 'admin' | 'staff' | 'viewer' | 'member' | null = null
      if (profile && !profile.is_super_admin) {
        const errorKey = `${supabaseUser.id}-${profile.organization_id}`

        const { data: roleData, error: roleError } = await fetchWithRetry(
          async () => {
            const { data: orgUsers, error: orgError } = await supabase
              .from('organization_users')
              .select('role, organization_id, user_id')
              .eq('user_id', supabaseUser.id)
              .eq('organization_id', profile.organization_id)
              .limit(1)

            if (orgError) {
              throw orgError
            }

            return orgUsers
          },
          { maxRetries: 2, baseDelayMs: 1000, context: 'organization role' }
        )

        if (roleData && roleData.length > 0) {
          orgRole = roleData[0].role as 'admin' | 'staff' | 'viewer' | 'member'
          console.log('🔐 Organization role loaded:', orgRole)
          // Clear error tracking on success
          orgRoleErrorLoggedRef.current = null
        } else if (roleError) {
          // Only log error once per user/org combination to prevent spam
          if (orgRoleErrorLoggedRef.current !== errorKey) {
            console.error('🔐 Failed to load organization role after retries:', {
              code: (roleError as { code?: string }).code,
              message: roleError.message,
              organizationId: profile.organization_id
            })
            orgRoleErrorLoggedRef.current = errorKey
          }
        } else if (profile.organization_id && orgRoleErrorLoggedRef.current !== errorKey) {
          // No error but no data - user not in organization_users (log once)
          console.warn('🔐 User has organization_id but no organization_users record:', {
            userId: supabaseUser.id,
            organizationId: profile.organization_id
          })
          orgRoleErrorLoggedRef.current = errorKey
        }
      }
      
      const user: User = {
        id: supabaseUser.id,
        email: supabaseUser.email || '',
        profile: profile,
        user_metadata: supabaseUser.user_metadata || {}
      }
      
      console.log('🔐 User object created with metadata:', {
        userId: user.id,
        hasProfile: !!user.profile,
        userMetadata: user.user_metadata,
        profileOrgId: profile?.organization_id
      })

      setUser(user)
      setProfile(profile)
      cacheProfile(profile) // Cache the profile
      setOrganizationRole(orgRole)
      console.log('🔐 User state updated:', {
        userId: user.id,
        email: user.email,
        profile: profile ? { id: profile.id, role: profile.role, is_super_admin: profile.is_super_admin } : null,
        orgRole
      })
    } catch (error) {
      console.error('🔐 Profile loading error:', error)
      
      // Still set basic user info even if profile fetch fails
      const user: User = {
        id: supabaseUser.id,
        email: supabaseUser.email || '',
        profile: null,
        user_metadata: supabaseUser.user_metadata
      }
      
      // Keep existing profile if we have one, don't clear it on error
      const existingProfile = currentProfile || getCachedProfile()
      if (existingProfile) {
        console.log('🔐 Using existing/cached profile after error')
        setProfile(existingProfile)
        setUser({ ...user, profile: existingProfile })
      } else {
        // Create a default profile to prevent lockout
        const defaultProfile = {
          id: supabaseUser.id,
          email: supabaseUser.email || '',
          role: 'admin' as const,
          full_name: supabaseUser.email || '',
          organization_id: KNOWN_ORGS.DEFAULT,
          is_super_admin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        console.log('🔐 Using default profile after error')
        setProfile(defaultProfile)
        setUser({ ...user, profile: defaultProfile })
        cacheProfile(defaultProfile)
      }
      setOrganizationRole('admin') // Default to admin to prevent lockout
    } finally {
      // Always ensure loading is false
      profileLoadingRef.current = false
      if (loading) {
        console.log('🔐 Forcing loading to false in loadUserProfile')
        setLoading(false)
      }
    }
  }

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true)
      
      console.log('🔐 AuthContext signIn called:', { email: email.trim().toLowerCase() })
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      })

      console.log('🔐 Supabase auth response:', { data: !!data.user, error: error?.message })

      if (error) {
        console.error('🔐 Supabase auth error:', error)

        // Special handling for email confirmation error
        if (error.message?.toLowerCase().includes('email not confirmed')) {
          return {
            error: 'Please confirm your email before signing in. Check your inbox for a confirmation link. If you clicked the link, please wait a moment and try again.'
          }
        }

        return { error: handleAuthError(error) }
      }

      if (data.user) {
        console.log('🔐 User authenticated, loading profile...')
        
        // Create immediate minimal profile to unblock UI
        // Use organization from user metadata or detect from subdomain
        const metadataOrgId = data.user.user_metadata?.organization_id
        const immediateOrgId = metadataOrgId || KNOWN_ORGS.DEFAULT

        const immediateProfile: Profile = {
          id: data.user.id,
          email: data.user.email || email,
          full_name: data.user.email || email,
          role: 'admin',
          organization_id: immediateOrgId,
          is_super_admin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        
        const immediateUser: User = {
          id: data.user.id,
          email: data.user.email || email,
          profile: immediateProfile
        }
        
        // Set immediately to unblock UI
        setUser(immediateUser)
        setProfile(immediateProfile)
        setOrganizationRole('admin')
        setLoading(false)
        
        // Then load real profile in background
        try {
          await loadUserProfile(data.user)
        } catch (profileError) {
          console.error('🔐 Profile load failed during sign in:', profileError)
          // Keep the immediate profile as fallback
        }
      }

      console.log('🔐 Sign in successful')
      return { error: null }
    } catch (error) {
      console.error('🔐 Sign in exception:', error)
      return { error: handleAuthError(error) }
    } finally {
      console.log('🔐 Setting loading to false after sign in')
      setLoading(false)
    }
  }

  // Sign up with email, password, full name, and organization name
  const signUp = async (email: string, password: string, fullName: string, organizationName: string) => {
    try {
      setLoading(true)

      // Check if there's a pending invitation
      const pendingInvitation = sessionStorage.getItem('pendingInvitation')
      let redirectUrl = `${window.location.origin}/ai-setup`

      if (pendingInvitation) {
        try {
          const invitation = JSON.parse(pendingInvitation)
          redirectUrl = `${window.location.origin}/accept-invitation?token=${invitation.token}`
        } catch (e) {
          console.error('Error parsing invitation:', e)
        }
      }

      // Use standard signUp - email confirmation should be disabled in Supabase dashboard
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            full_name: fullName.trim(),
            organization_name: organizationName.trim()
          },
          emailRedirectTo: redirectUrl
        }
      })

      if (error) {
        return { error: handleAuthError(error) }
      }

      // Organization setup is now handled automatically by database trigger:
      // 1. Create the organization using the organization_name from user metadata
      // 2. Create a 14-day trial subscription
      // 3. Link the user as admin
      // Note: Profile is also automatically created by database trigger
      if (data.user) {
        console.log('✅ User created successfully. Organization will be created automatically.')

        // For invitation flow, we still need to load the profile
        if (pendingInvitation) {
          await loadUserProfile(data.user)
        }

        // Send welcome email
        try {
          // Get organization name if user is accepting an invitation
          let inviteOrgName = undefined
          if (pendingInvitation) {
            try {
              const invitation = JSON.parse(pendingInvitation)
              inviteOrgName = invitation.organizationName
            } catch (e) {
              console.error('Error parsing invitation for email:', e)
            }
          }

          await sendWelcomeEmail({
            to: data.user.email || email,
            fullName: fullName.trim(),
            organizationName: inviteOrgName
          })
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError)
          // Don't fail signup if email fails
        }
      }

      return { error: null }
    } catch (error) {
      return { error: handleAuthError(error) }
    } finally {
      setLoading(false)
    }
  }

  // Sign out
  const signOut = async () => {
    try {
      setLoading(true)
      
      const { error } = await supabase.auth.signOut()
      
      if (error) {
        // Sign out error handled silently
      }

      // Clear local state
      setUser(null)
      setProfile(null)
      localStorage.removeItem('cached_profile')

      // Clear ALL cached query data to prevent cross-account leakage
      queryClient.clear()

      // Clear monitoring user context
      clearUserContext()
    } catch (error) {
      // Sign out process error handled silently
    } finally {
      setLoading(false)
    }
  }

  // Update user profile
  const updateProfile = async (updates: Partial<Profile>) => {
    try {
      if (!user?.id) {
        return { error: 'No user logged in' }
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)

      if (error) {
        // Profile update error
        return { error: handleAuthError(error) }
      }

      // Reload profile to get updated data
      await loadUserProfile({ id: user.id, email: user.email } as SupabaseUser)

      return { error: null }
    } catch (error) {
      return { error: handleAuthError(error) }
    }
  }

  // Role-based access helpers
  const isSuperAdmin = profile?.is_super_admin === true
  const isOrganizationAdmin = organizationRole === 'admin' || isSuperAdmin
  const isOrganizationStaff = organizationRole === 'staff' || isOrganizationAdmin
  const isOrganizationViewer = organizationRole === 'viewer' || isOrganizationStaff
  
  // Legacy role helpers (fall back to organization_users role)
  const isAdmin = profile?.role === 'admin' || isOrganizationAdmin
  const isStaff = profile?.role === 'staff' || isAdmin || isOrganizationStaff
  const isMember = profile?.role === 'member' || organizationRole === 'member'

  // Check if user has any of the specified roles
  // Uses organizationRole from organization_users table (single source of truth)
  const hasRole = (roles: UserRole[]): boolean => {
    // Demo accounts can access all routes (admin + customer portal)
    if (isDemoAccount(user?.email)) return true

    if (!organizationRole) return false

    // Super admin has access to everything
    if (isSuperAdmin) return true

    // Admin has access to everything
    if (organizationRole === 'admin') return true

    return roles.includes(organizationRole as UserRole)
  }

  // Check if user has organization permission
  const hasOrganizationPermission = (permission: 'admin' | 'staff' | 'viewer'): boolean => {
    if (isSuperAdmin) return true
    
    if (permission === 'viewer') return isOrganizationViewer
    if (permission === 'staff') return isOrganizationStaff
    if (permission === 'admin') return isOrganizationAdmin
    
    return false
  }

  // Context value
  const value: AuthContextType = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAdmin,
    isStaff,
    isMember,
    hasRole,
    isSuperAdmin,
    isOrganizationAdmin,
    isOrganizationStaff,
    isOrganizationViewer,
    organizationRole,
    hasOrganizationPermission
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext)
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  
  return context
}

// Hook for role-based access control
export function useRequireAuth(requiredRoles?: UserRole[]) {
  const auth = useAuth()
  
  useEffect(() => {
    if (!auth.loading && !auth.user) {
      // Redirect to login if not authenticated
      window.location.href = '/login'
      return
    }

    if (requiredRoles && auth.profile && !auth.hasRole(requiredRoles)) {
      // Redirect to unauthorized page if role doesn't match
      window.location.href = '/unauthorized'
      return
    }
  }, [auth.loading, auth.user, auth.profile, requiredRoles])

  return auth
}

// Hook for conditional rendering based on roles
export function useRoleAccess() {
  const { profile, isAdmin, isStaff, isMember, hasRole } = useAuth()
  
  return {
    role: profile?.role,
    isAdmin,
    isStaff,
    isMember,
    hasRole,
    canAccess: (roles: UserRole[]) => hasRole(roles),
    canManage: (resource: string) => {
      switch (resource) {
        case 'users':
        case 'settings':
        case 'facilities':
          return isAdmin
        case 'bookings':
        case 'members':
        case 'invoices':
        case 'communications':
          return isStaff
        case 'profile':
        case 'own-bookings':
        case 'own-invoices':
          return true // All authenticated users
        default:
          return false
      }
    }
  }
}